import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faFile, faTimes, faQuestionCircle, faSpinner, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import './knowledgeModal.css';
import { fetchKnowledgePreview, KnowledgePreviewNotice } from './knowledgePreview.js';

// `title` (optional) overrides the fetched document_name/url_link heading —
// callers that know a human-readable page name (e.g. the Shopify store-content
// panel, whose rows are url_link knowledge without a document_name) pass it here.
const KnowledgeModal = ({ open, onClose, knowledgeId, title: titleOverride }) => {
  const { t } = useTranslation();
  const [plainText, setPlainText] = useState(null);
  const [title, setTitle] = useState('');
  const [urlLink, setUrlLink] = useState(null);
  const [knowledgeType, setKnowledgeType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Large items come back cut to PREVIEW_MAX_CHARS; the notice offers the rest.
  const [totalChars, setTotalChars] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);

  useEffect(() => {
    if (open && knowledgeId) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
          const data = await fetchKnowledgePreview(knowledgeId);
          setPlainText(data.relevant_data);
          setTotalChars(data.total_chars || 0);
          setTruncated(!!data.truncated);
          setTitle(data.document_name || data.url_link || t('knowledge_default_plaintext_title'));
          setKnowledgeType(data.type);
          if (data.type === 'url_link' && data.url_link) setUrlLink(data.url_link);
        } catch (err) {
          console.error('Error fetching knowledge data:', err);
          setError(t('knowledge_load_content_failed'));
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [knowledgeId, open]);

  const handleLoadFull = async () => {
    setLoadingFull(true);
    try {
      const data = await fetchKnowledgePreview(knowledgeId, { full: true });
      setPlainText(data.relevant_data);
      setTruncated(false);
    } catch (err) {
      console.error('Error loading full knowledge text:', err);
      setError(t('knowledge_load_content_failed'));
    } finally {
      setLoadingFull(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="knowledge-modal-title"
      aria-describedby="knowledge-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          maxHeight: '80%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          overflow: 'hidden',
          borderRadius: '8px',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div className="knowledge-modal-header">
          <h2 className="knowledge-modal-header-title">
            <FontAwesomeIcon
              icon={knowledgeType === 'url_link' ? faGlobe : knowledgeType === 'faq' ? faQuestionCircle : faFile}
              className="knowledge-modal-type-icon"
            />
            {titleOverride || title}
            {urlLink && (
              <a href={urlLink} target="_blank" rel="noopener noreferrer" className="knowledge-modal-external-link">
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </a>
            )}
          </h2>
          <button className="knowledge-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Loading and Error Handling */}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#5616ea" />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            {truncated && (
              <KnowledgePreviewNotice
                shownChars={(plainText || '').length}
                totalChars={totalChars}
                onLoadFull={handleLoadFull}
                loadingFull={loadingFull}
              />
            )}
            <div className="knowledge-modal-body">
              <pre className="knowledge-plain-text">{plainText}</pre>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default KnowledgeModal;
